import { format } from 'date-fns';

import type { Locale } from 'date-fns';

/**
 * Date format variant
 *
 * - `'dayMonth'` - "January 15" / "15 січня"
 * - `'full'` - "January 15, 2025" / "15 січня 2025"
 * - `'fullWithTime'` - "January 15, 2025 at 14:30" / "15 січня 2025, 14:30"
 * - `'long'` - "Jan 15, 2025" / "15 січ 2025"
 * - `'longWithTime'` - "Jan 15, 2025 14:30" / "15 січ 2025 14:30"
 * - `'medium'` - "01/15/2025" / "15.01.2025"
 * - `'mediumWithTime'` - "01/15/2025 14:30" / "15.01.2025 14:30"
 * - `'monthYear'` - "January 2025" / "Січень 2025"
 * - `'numeric'` - "20250115"
 * - `'short'` - "01/15/25" / "15.01.25"
 * - `'shortWithTime'` - "01/15/25 14:30" / "15.01.25 14:30"
 * - `'weekday'` - "Monday" / "Понеділок"
 */
type FormatVariant =
  | 'dayMonth'
  | 'dayMonthWeekday'
  | 'full'
  | 'fullWithTime'
  | 'long'
  | 'longWithTime'
  | 'medium'
  | 'mediumWithTime'
  | 'monthYear'
  | 'numeric'
  | 'short'
  | 'shortWithTime'
  | 'weekday';

type FormatDateOptions = {
  /** Custom date-fns format string (overrides variant) */
  customFormat?: string;
  /** date-fns locale object */
  locale?: Locale;
  /**
   * Predefined format variant
   *
   * - `'dayMonth'` - "January 15" / "15 січня"
   * - `'full'` - "January 15, 2025" / "15 січня 2025"
   * - `'fullWithTime'` - "January 15, 2025 at 14:30" / "15 січня 2025, 14:30"
   * - `'long'` - "Jan 15, 2025" / "15 січ 2025"
   * - `'longWithTime'` - "Jan 15, 2025 14:30" / "15 січ 2025 14:30"
   * - `'medium'` - "01/15/2025" / "15.01.2025"
   * - `'mediumWithTime'` - "01/15/2025 14:30" / "15.01.2025 14:30"
   * - `'monthYear'` - "January 2025" / "Січень 2025"
   * - `'numeric'` - "20250115"
   * - `'short'` - "01/15/25" / "15.01.25"
   * - `'shortWithTime'` - "01/15/25 14:30" / "15.01.25 14:30"
   * - `'weekday'` - "Monday" / "Понеділок"
   */
  variant?: FormatVariant;
};

const localeFormats = {
  en: {
    dayMonth: 'MMMM d',
    dayMonthWeekday: 'MMMM d, EEEE',
    full: 'MMMM d, yyyy',
    fullWithTime: "MMMM d, yyyy 'at' HH:mm",
    long: 'MMM d, yyyy',
    longWithTime: 'MMM d, yyyy HH:mm',
    medium: 'MM/dd/yyyy',
    mediumWithTime: 'MM/dd/yyyy HH:mm',
    monthYear: 'MMMM yyyy',
    numeric: 'yyyyMMdd',
    short: 'MM/dd/yy',
    shortWithTime: 'MM/dd/yy HH:mm',
    weekday: 'EEEE',
  },
  uk: {
    dayMonth: 'd MMMM',
    dayMonthWeekday: 'd MMMM, EEEE',
    full: 'd MMMM yyyy',
    fullWithTime: 'd MMMM yyyy, HH:mm',
    long: 'd MMM yyyy',
    longWithTime: 'd MMM yyyy HH:mm',
    medium: 'dd.MM.yyyy',
    mediumWithTime: 'dd.MM.yyyy HH:mm',
    monthYear: 'LLLL yyyy',
    numeric: 'yyyyMMdd',
    short: 'dd.MM.yy',
    shortWithTime: 'dd.MM.yy HH:mm',
    weekday: 'EEEE',
  },
} as const satisfies Record<string, Record<FormatVariant, string>>;

/**
 * Formats a date according to locale and variant
 *
 * @param dateString - Date to format (Date object or ISO string)
 * @param options - Formatting options
 * @param options.locale - date-fns locale object (defaults to 'uk')
 * @param options.variant - Predefined format variant (defaults to 'full')
 * @param options.customFormat - Custom date-fns format string (overrides variant)
 *
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * // Full date
 * formatDate(new Date(), { locale: uk, variant: 'full' })
 * // => "15 січня 2025"
 *
 * // Full date with time
 * formatDate(new Date(), { locale: uk, variant: 'fullWithTime' })
 * // => "15 січня 2025, 14:30"
 *
 * // Short date
 * formatDate(new Date(), { locale: uk, variant: 'short' })
 * // => "15.01.25"
 *
 * // Custom format
 * formatDate(new Date(), { locale: uk, customFormat: 'dd/MM/yyyy' })
 * // => "15/01/2025"
 * ```
 */
export function formatDate(
  dateString: Date | string,
  { customFormat, locale, variant = 'full' }: FormatDateOptions = {},
): string {
  const localeCode = (locale?.code ?? 'uk') as keyof typeof localeFormats;
  const dateFormat = localeFormats[localeCode]?.[variant] ?? localeFormats.uk[variant];

  return format(dateString, customFormat ?? dateFormat, { locale });
}
